import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import keycloak, { initKeycloak, beginReauth, endReauth } from '../lib/keycloak';
import { setMissingAccountScopeHandler } from '../services/api';
import { useAccountUsage } from '../hooks/useAccountUsage';
import { applyAnalyticsIdentity, resetAnalyticsIdentity } from '../lib/analytics';
import { syncAnalyticsConsent, clearPreferenceCache } from '../lib/analyticsConsent';
import { isAuthOptionalPath, isRegisterPath } from '../utils/publicPolicyPaths';
import type Keycloak from 'keycloak-js';

// ─── JWT Claim Types ──────────────────────────────────────────────────────────

/**
 * One account membership as carried by the `accounts` claim (task 676) — every
 * account the User belongs to, with the role they hold in each. The selected
 * account still travels separately in `account_id`/`account_slug`/`account_role`;
 * this list is the whole picture.
 */
export interface AccountMembership {
  /** AMS account UUID. */
  id: string;
  /** URL-grade account slug. */
  slug: string;
  /** The User's AMS role in this account (e.g. `owner`, `member`). */
  role: string;
}

/**
 * Raw Keycloak JWT token claims as decoded by keycloak-js.
 * Custom AMS claims are injected by the AMS Keycloak SPI (AmsClaimsMapper).
 * Authorization is driven by AMS account roles, NOT by Keycloak
 * realm_access.roles.
 */
interface KeycloakTokenClaims {
  sub?: string;
  email?: string;
  email_verified?: boolean;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
  // AMS account context claims (injected by AmsClaimsMapper SPI)
  user_id?: string;
  account_id?: string;
  /**
   * URL-grade slug of the caller's AMS account (task 383). Optional AND
   * nullable on purpose: it is `null` for a user with no membership, and
   * absent entirely on tokens minted before the claim shipped. Every consumer
   * must tolerate both — see `AuthUser.accountSlug`.
   */
  account_slug?: string | null;
  /**
   * Name of the caller's AMS organization, for the analytics profile trait
   * (task 1296). Optional: absent on tokens whose mapper does not emit it, in
   * which case the trait is dropped silently.
   */
  organization_name?: string;
  account_role?: string;
  /**
   * Every account membership with its role (task 676). Absent on tokens minted
   * before the claim shipped — consumers must tolerate `undefined` and fall
   * back to the single selected account.
   */
  accounts?: AccountMembership[];
  /**
   * Platform-scope roles (e.g. `platform_admin`, `platform_support`) for
   * Monarch staff. The AMS SPI injects the claim ONLY when it is non-empty, so
   * customers' tokens omit it entirely — presence is the "internal user"
   * signal used to derive the analytics `audience` property (task 426).
   */
  platform_roles?: string[];
  status?: string;
  /**
   * AMS provisioning status. When `"not_provisioned"`, the Keycloak user
   * has no backing AMS account (e.g. created via seed job only). Emitted
   * by the SPI after task 067 ships.
   */
  ams_status?: string;
  /**
   * AMS error signal. `"unavailable"` means AMS was unreachable when the
   * token was minted, so account context could not be resolved — distinct
   * from a user who genuinely has no account.
   */
  ams_error?: string;
  /**
   * True when the user has NOT accepted the current version of every required
   * policy document (Terms of Use, Privacy Policy) — computed by AMS
   * (`computeConsentPending`) and injected by AmsClaimsMapper (task 420).
   * Drives the ConsentGate interstitial (task 422). Absent on tokens minted
   * before the claim shipped — treated as not pending (fail-open, since those
   * users predate the consent feature and were backfilled).
   */
  consent_pending?: boolean;
}

// ─── Public Auth User ─────────────────────────────────────────────────────────

export interface AuthUser {
  /** Keycloak sub (UUID) */
  sub: string;
  email: string;
  emailVerified: boolean;
  firstName: string;
  lastName: string;
  username: string;
  /** AMS user UUID — distinct from Keycloak sub */
  userId?: string;
  /** AMS account UUID — the single tenancy key */
  accountId?: string;
  /**
   * URL-grade slug of the caller's AMS account (task 383) — the `{accountSlug}`
   * segment in `/api/{accountSlug}/{path}`.
   *
   * `undefined` when the token predates the claim, `null` when the user has no
   * account membership. Use it ONLY for create-time previews and path-prefix
   * adornment; when rendering a URL for a *specific* endpoint always use that
   * endpoint row's own `accountSlug` (correct even for system endpoints).
   */
  accountSlug?: string | null;
  /**
   * AMS organization name (task 1296), used only as an analytics profile
   * trait. `undefined` whenever the token does not carry the claim.
   */
  organizationName?: string;
  /**
   * AMS account role — "owner" for the initial implementation.
   * This is the AMS claim, NOT a Keycloak realm role.
   */
  accountRole?: string;
  /**
   * Every account the User belongs to, with their role in each (task 676).
   *
   * `undefined` on tokens minted before the `accounts` claim shipped, so every
   * consumer must tolerate absence and fall back to the selected account
   * (`accountId`/`accountSlug`/`accountRole`). Carried additively for now — no
   * UI consumes it yet; an account switcher would be its own feature task.
   */
  accounts?: AccountMembership[];
  /**
   * Platform-scope roles from AMS (Monarch staff only). Absent/empty for
   * customers — the SPI omits the claim entirely for them. Optional so
   * existing AuthUser literals stay valid.
   */
  platformRoles?: string[];
  /** AMS user status */
  status?: string;
  /**
   * True only when an AMS account is provisioned for this user — i.e. the
   * token carries an `account_id` and is NOT flagged `ams_status=not_provisioned`.
   * When false, the user is authenticated with Keycloak but has no usable
   * account; every account-scoped API call would return 403.
   */
  isAccountProvisioned: boolean;
  /**
   * True when AMS was unavailable at token-mint time (`ams_error=unavailable`).
   * Distinct from "no account" — this is a transient service outage, not a
   * missing account. UI should surface a "try again later" state.
   */
  isAmsUnavailable: boolean;
  /**
   * True when the token carries `consent_pending=true` — the user must accept
   * the current Terms of Use + Privacy Policy before using the app (task 422).
   * Optional so pre-existing fixtures/tokens without the claim stay valid;
   * absent means not pending. NOTE: the AMS SPI claims cache (~30s) means this
   * can stay true briefly after a successful acceptance — ConsentGate keeps its
   * own session-scoped acknowledgement and must never re-prompt on it.
   */
  consentPending?: boolean;
}

// ─── Context Type ─────────────────────────────────────────────────────────────

export type AuthError = 'redirect_failed' | 'init_timeout' | null;

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  /**
   * True when the user has a valid Keycloak session AND a provisioned AMS
   * account. False when authenticated but the token carries no `account_id`
   * (or `ams_status=not_provisioned`), or when a 403 MISSING_ACCOUNT_SCOPE
   * fallback has fired. Does NOT affect `isAuthenticated`.
   */
  isAccountProvisioned: boolean;
  /** True when AMS was unavailable at token-mint time (`ams_error=unavailable`). */
  isAmsUnavailable: boolean;
  isLoading: boolean;
  login: () => void;
  /**
   * Start a Keycloak flow with explicit options (task 731) — the invitation
   * landing page's "Create your account" and "Sign in" actions.
   *
   *   - `action: 'register'` opens Keycloak's REGISTRATION form directly
   *     instead of the sign-in form the automatic gate redirect uses.
   *   - `loginHint` prefills the email, so an invited User never retypes the
   *     address the invitation is keyed on (typing a different one silently
   *     forks a fresh account).
   *   - `redirectUri` defaults to the current URL, preserving `?token=…`.
   *
   * Deliberately additive: `login()` and the gate redirect are unchanged, so no
   * other route's behaviour moves.
   */
  loginWithOptions: (options?: {
    action?: 'register';
    loginHint?: string;
    redirectUri?: string;
  }) => void;
  logout: () => void;
  /**
   * Force-refresh the Keycloak access token. Throws on failure.
   */
  refreshToken: () => Promise<void>;
  authError: AuthError;
  clearAuthError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

/**
 * The caller's own account slug (task 385), or `undefined` when unknown —
 * outside an AuthProvider, before the token resolves, or on a token minted
 * before the `account_slug` claim shipped.
 *
 * Deliberately non-throwing (unlike `useAuthContext`): the slug is a display
 * affordance for create-time previews and path prefixes, so a component that
 * renders without auth must degrade to the legacy unqualified shape rather
 * than crash. Never use this to build a URL for an existing endpoint — that
 * comes from the endpoint row's own `accountSlug`.
 */
export const useAccountSlug = (): string | undefined => {
  const context = useContext(AuthContext);
  return context?.user?.accountSlug ?? undefined;
};

/**
 * Force-refresh accessor that tolerates a missing AuthProvider.
 *
 * Same shape as `useAccountSlug`: components and hooks are rendered without an
 * AuthProvider in the component test suite, so a hook that only *optionally*
 * needs auth must not use `useAuthContext()` — that throws. Returns undefined
 * when there is no provider, and the caller skips the refresh.
 */
export const useOptionalRefreshToken = (): (() => Promise<void>) | undefined => {
  const context = useContext(AuthContext);
  return context?.refreshToken;
};

/**
 * Whole-context accessor that tolerates a missing AuthProvider (task 467).
 *
 * Same rationale as `useAccountSlug` / `useOptionalRefreshToken`: gating hooks
 * like `useRequiresPlan` run inside components that the component test suite
 * renders without an AuthProvider, so they must degrade (fail-open) rather
 * than crash. Returns undefined when there is no provider.
 */
export const useOptionalAuthContext = (): AuthContextType | undefined =>
  useContext(AuthContext);

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Build an AuthUser from Keycloak token claims.
 * Reads AMS claims (user_id, account_id, account_role, accounts[], status)
 * from the decoded token. Does NOT read realm_access.roles — platform
 * authorization keys off AMS claims only.
 */
export function buildAuthUser(kc: Keycloak): AuthUser | null {
  const parsed = kc.tokenParsed as KeycloakTokenClaims | undefined;
  if (!parsed || !parsed.sub) return null;

  // "No account" = account_id absent OR explicitly flagged not_provisioned.
  // An account is provisioned only when account_id is present AND not flagged.
  const isAccountProvisioned =
    Boolean(parsed.account_id) && parsed.ams_status !== 'not_provisioned';

  // AMS temporarily down at token-mint time — kept separate from "no account"
  // so the UI can distinguish a transient outage from a missing account.
  const isAmsUnavailable = parsed.ams_error === 'unavailable';

  return {
    sub: parsed.sub,
    email: parsed.email ?? '',
    emailVerified: parsed.email_verified === true,
    firstName: parsed.given_name ?? '',
    lastName: parsed.family_name ?? '',
    username: parsed.preferred_username ?? '',
    userId: parsed.user_id,
    accountId: parsed.account_id,
    // Absent claim stays undefined (older tokens); an explicit null (no
    // membership) is preserved as null so consumers can tell them apart.
    accountSlug: parsed.account_slug,
    organizationName: parsed.organization_name || undefined,
    accountRole: parsed.account_role,
    // Absent on pre-task-677 tokens — left undefined rather than defaulted to
    // [] so consumers can tell "claim not present" from "no memberships".
    accounts: parsed.accounts,
    platformRoles: parsed.platform_roles ?? [],
    status: parsed.status,
    isAccountProvisioned,
    isAmsUnavailable,
    // Absent claim = not pending (older tokens predate the consent feature).
    consentPending: parsed.consent_pending === true,
  };
}

// ─── AuthProvider ─────────────────────────────────────────────────────────────

interface AuthProviderProps {
  children: React.ReactNode;
}

/** Minimum seconds remaining on token before proactively refreshing. */
const TOKEN_MIN_VALIDITY_SECONDS = 60;

/** How often (ms) to check token validity for proactive refresh. */
const TOKEN_REFRESH_INTERVAL_MS = 30_000;

/**
 * Maximum milliseconds to wait for `keycloak.init()` to resolve.
 * Passive init should resolve quickly. A hang is a symptom of keycloak-js
 * attempting a background request in a context that has no session cookies.
 */
const KEYCLOAK_INIT_TIMEOUT_MS = 5_000;

/**
 * Backoff (ms) between token-refresh retries (task 1037). keycloak-js rejects
 * the same way for a network error (laptop waking, Wi-Fi flapping) and for a
 * definitive `invalid_grant`, so a failure is retried this many times before
 * the session is treated as dead. A definitive failure costs nothing extra:
 * keycloak-js drops the refresh token on a 400, so later attempts fail at once.
 */
const TOKEN_REFRESH_RETRY_BACKOFF_MS = [2_000, 4_000, 8_000];

/**
 * AuthProvider — Keycloak OIDC authentication using a module-scope singleton.
 *
 * The Keycloak instance and its init promise are created in `src/lib/keycloak.ts`
 * so that React StrictMode's double-mount is harmless (the second call returns
 * the same cached promise). Initialisation is passive — no `onLoad` option — so
 * Keycloak only processes auth callback params already in the URL and never
 * proactively redirects during init.
 *
 * Token lifecycle:
 * - A periodic interval proactively refreshes the token when less than
 *   TOKEN_MIN_VALIDITY_SECONDS remain.
 * - keycloak.onTokenExpired fires if the token expires before the interval
 *   runs, triggering an immediate refresh.
 * - A failed refresh is retried (TOKEN_REFRESH_RETRY_BACKOFF_MS, paused while
 *   offline), then `handleRefreshFailure()` clears the local tokens and
 *   re-enters login. It never calls `keycloak.logout()`: the end-session call
 *   deletes the browser's shared Keycloak root authentication session, which
 *   breaks sign-in forms open in other Monarch Works tabs (task 1037).
 * - If init completes without authentication, the user is redirected to
 *   Keycloak login (login-required behavior).
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<AuthError>(null);
  /**
   * Fallback signal from the API layer: a 403 MISSING_ACCOUNT_SCOPE response
   * means the backend rejected an account-scoped call even though the token
   * looked valid (e.g. a stale token whose claims predate provisioning). When
   * set, it forces the unprovisioned guard regardless of the decoded claim.
   */
  const [accountScopeRejected, setAccountScopeRejected] = useState(false);

  // ── Token helpers ─────────────────────────────────────────────────────────

  const applyToken = useCallback(() => {
    const newToken = keycloak.token ?? null;
    setToken(newToken);
    if (newToken) {
      // Persist all three tokens so keycloak-js can rehydrate on a fresh
      // navigation (browser refresh). See `lib/keycloak.ts` — init() reads
      // these back and treats the session as live.
      sessionStorage.setItem('kc_token', newToken);
      if (keycloak.refreshToken) {
        sessionStorage.setItem('kc_refreshToken', keycloak.refreshToken);
      }
      if (keycloak.idToken) {
        sessionStorage.setItem('kc_idToken', keycloak.idToken);
      }
    } else {
      sessionStorage.removeItem('kc_token');
      sessionStorage.removeItem('kc_refreshToken');
      sessionStorage.removeItem('kc_idToken');
    }
    setUser(buildAuthUser(keycloak));
  }, []);

  /**
   * A token refresh has failed for good (task 1037). Drop the local tokens and
   * re-enter login — never `keycloak.logout()`. When the Keycloak SSO session
   * is dead, the end-session round trip achieves nothing, but it deletes the
   * browser's shared Keycloak root authentication session (holding other tabs'
   * pending sign-in forms) and expires `KC_RESTART`, so the User's next
   * sign-in lands on Keycloak's "please login again through your application"
   * dead end. Re-entering login instead signs in silently while the SSO cookie
   * is alive, and shows the sign-in form once when it is not.
   *
   * Guarded by the module-level `reauthInFlight` flag in `lib/keycloak.ts`,
   * shared with the 401 interceptor, so only one redirect ever starts.
   */
  const handleRefreshFailure = useCallback(() => {
    if (!beginReauth()) {
      console.warn('[AuthContext] Re-authentication already in flight — not starting another.');
      return;
    }
    sessionStorage.removeItem('kc_token');
    sessionStorage.removeItem('kc_refreshToken');
    sessionStorage.removeItem('kc_idToken');
    // Fires onAuthLogout (clears user/token state) when a token is still held;
    // a no-op when keycloak-js already cleared it on a 400.
    keycloak.clearToken();
    // Clear React Query cache so no stale data leaks into the next session.
    queryClient.clear();
    Promise.resolve(keycloak.login({ redirectUri: window.location.href })).catch((err) => {
      console.error('[AuthContext] Re-authentication redirect failed:', err);
      endReauth();
    });
  }, [queryClient]);

  // ── Initialisation ────────────────────────────────────────────────────────

  useEffect(() => {
    // The location the visitor ARRIVED on, captured synchronously at mount.
    //
    // Read once on purpose (task 792): building a Keycloak login URL is async
    // (keycloak-js hashes the PKCE verifier), so React renders — and React
    // Router can rewrite the URL — while the redirect is still in flight. On
    // `/register` the authenticated route's `<Navigate to="/">` does exactly
    // that. Deciding from `window.location` at resolve time therefore saw `/`
    // and fired the plain SIGN-IN redirect instead of registration, which was
    // observed flaking live on the local stack.
    const entryPathname = window.location.pathname;
    const entrySearch = window.location.search;

    /** Set on unmount so a pending retry loop stops instead of acting. */
    let disposed = false;

    /** The retry loop in progress, joined by any caller that arrives meanwhile. */
    let refreshInFlight: Promise<void> | null = null;

    const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

    /** Resolves at once when online; otherwise on the next `online` event. */
    const waitUntilOnline = () =>
      navigator.onLine === false
        ? new Promise<void>((resolve) =>
            window.addEventListener('online', () => resolve(), { once: true })
          )
        : Promise.resolve();

    /**
     * Refresh the token, retrying transient failures with backoff before
     * handing a dead session to `handleRefreshFailure()` (task 1037). While the
     * browser reports offline no attempt is made, so no retry is spent.
     */
    const refreshWithRetry = (
      source: 'proactive-interval' | 'onTokenExpired' | 'onAuthRefreshError'
    ) => {
      if (refreshInFlight) return refreshInFlight;
      const maxAttempts = TOKEN_REFRESH_RETRY_BACKOFF_MS.length + 1;
      refreshInFlight = (async () => {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          await waitUntilOnline();
          if (disposed) return;
          try {
            const refreshed = await keycloak.updateToken(TOKEN_MIN_VALIDITY_SECONDS);
            if (disposed) return;
            if (refreshed) applyToken();
            if (attempt > 1) {
              console.warn(`[AuthContext] Token refresh (${source}) recovered on attempt ${attempt}.`);
            }
            return;
          } catch {
            if (disposed) return;
            // keycloak-js drops the refresh token on a definitive 400
            // (invalid_grant): nothing left to retry with.
            if (!keycloak.refreshToken) {
              console.warn(
                `[AuthContext] Token refresh (${source}) failed definitively (refresh token revoked) — skipping retries.`
              );
              break;
            }
            if (attempt === maxAttempts) break;
            const delay = TOKEN_REFRESH_RETRY_BACKOFF_MS[attempt - 1];
            console.warn(
              `[AuthContext] Token refresh (${source}) failed, attempt ${attempt} of ${maxAttempts} — retrying in ${delay / 1000}s.`
            );
            await sleep(delay);
            if (disposed) return;
          }
        }
        console.error(`[AuthContext] Token refresh (${source}) gave up — re-entering login.`);
        handleRefreshFailure();
      })().finally(() => {
        refreshInFlight = null;
      });
      return refreshInFlight;
    };

    // Wire event hooks on the singleton before calling init.
    keycloak.onTokenExpired = () => {
      void refreshWithRetry('onTokenExpired');
    };

    keycloak.onAuthRefreshSuccess = () => {
      applyToken();
    };

    // keycloak-js fires this on EVERY failed updateToken attempt, transient or
    // not, before rejecting the caller's promise. Never keycloak.logout()
    // (task 1037):
    //   - inside a retry loop: do nothing, the loop decides;
    //   - outside one (a 401-path or forced refresh failed) with the refresh
    //     token gone: a definitive failure, re-enter login now;
    //   - otherwise: a possibly transient failure, start the retry loop.
    keycloak.onAuthRefreshError = () => {
      if (refreshInFlight) {
        console.warn('[AuthContext] Auth refresh error (onAuthRefreshError) — retry loop in progress, it decides.');
        return;
      }
      if (!keycloak.refreshToken) {
        console.error('[AuthContext] Auth refresh error (onAuthRefreshError) — refresh token revoked, re-entering login.');
        handleRefreshFailure();
        return;
      }
      console.warn('[AuthContext] Auth refresh error (onAuthRefreshError) — starting retry loop.');
      void refreshWithRetry('onAuthRefreshError');
    };

    keycloak.onAuthLogout = () => {
      setUser(null);
      setToken(null);
      sessionStorage.removeItem('kc_token');
      sessionStorage.removeItem('kc_refreshToken');
      sessionStorage.removeItem('kc_idToken');
    };

    // initKeycloak() returns a cached promise — safe to call multiple times.
    // Race it against a timeout so that a hung init does not leave
    // isLoading=true indefinitely.
    const initTimeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(
        () => reject(new Error('keycloak_init_timeout')),
        KEYCLOAK_INIT_TIMEOUT_MS
      );
    });

    /**
     * Set once the registration redirect has been handed to keycloak-js, to
     * keep AuthGate on its "Signing in..." screen until the browser leaves.
     *
     * Without this the tree mounts for a visitor who is provably anonymous, its
     * data hooks 401, and the 401 interceptor in `services/api.ts` fires its
     * OWN plain sign-in redirect — which beat the registration redirect on
     * roughly half the local runs, landing marketing traffic on the sign-in
     * form this entry path exists to bypass. Scoped to this one branch so no
     * other route's loading behaviour moves.
     */
    let redirectingToRegistration = false;

    Promise.race([initKeycloak(), initTimeoutPromise])
      .then((authenticated) => {
        if (authenticated) {
          applyToken();
        } else if (isAuthOptionalPath(entryPathname)) {
          // Auth-optional route (task 731): the invitation landing page must
          // render for a visitor who has no login yet — bouncing them to
          // Keycloak is precisely the defect this replaces. Every other path
          // keeps the login-required behaviour below.
        } else if (isRegisterPath(entryPathname)) {
          // Registration entry path (task 792): a visitor who followed a
          // marketing CTA has no login yet, so send them to Keycloak's
          // REGISTRATION form rather than its sign-in form. keycloak-js has to
          // be what starts the flow because the client requires PKCE.
          //
          // The return target is the app ROOT, not `/register` — coming back
          // here would re-enter this branch and bounce them out to Keycloak a
          // second time. `?login_hint=` is carried through so a website form
          // can prefill the email; nothing else on the query string is read.
          const loginHint = new URLSearchParams(entrySearch).get('login_hint');
          redirectingToRegistration = true;
          keycloak.login({
            action: 'register',
            redirectUri: `${window.location.origin}/`,
            ...(loginHint ? { loginHint } : {}),
          });
        } else {
          // Not authenticated — redirect to Keycloak login (login-required).
          keycloak.login({ redirectUri: window.location.href });
        }
      })
      .catch((err) => {
        const isTimeout =
          err instanceof Error && err.message === 'keycloak_init_timeout';
        if (isTimeout) {
          console.warn('[AuthContext] Keycloak init timed out.');
          setAuthError('init_timeout');
        } else {
          console.error('[AuthContext] Keycloak init error:', err);
          setAuthError('redirect_failed');
        }
      })
      .finally(() => {
        // Hold the spinner while leaving for the registration form (see above).
        if (!redirectingToRegistration) {
          setIsLoading(false);
        }
      });

    // Proactive token refresh interval (~every 30s)
    const refreshTimer = setInterval(() => {
      if (!keycloak.authenticated) return;
      void refreshWithRetry('proactive-interval');
    }, TOKEN_REFRESH_INTERVAL_MS);

    return () => {
      disposed = true;
      clearInterval(refreshTimer);
    };
  }, [applyToken, handleRefreshFailure]);

  // ── 403 MISSING_ACCOUNT_SCOPE fallback ──────────────────────────────────────
  // Register a handler so the axios response interceptor can flip the app into
  // the unprovisioned state without importing AuthContext (avoids a circular
  // dependency — the interceptor already imports the keycloak singleton only).
  useEffect(() => {
    setMissingAccountScopeHandler(() => setAccountScopeRejected(true));
    return () => setMissingAccountScopeHandler(null);
  }, []);

  // ── Account plan, for the analytics `plan` property (task 676) ──────────────
  // The plan claim is gone, so the property is sourced from the shared
  // account-usage query rather than a fetch of its own — this observer joins
  // the same cache entry the plan-driven UI already populates. Gated on being
  // authenticated so it never fires before a bearer token exists.
  const isAuthenticated = Boolean(user && token);
  const { data: usageForAnalytics } = useAccountUsage({ enabled: isAuthenticated });
  const analyticsPlan = usageForAnalytics?.plan ?? undefined;

  // ── Analytics identity (task 426; readable traits, task 1296) ─────────────
  // Attach the Keycloak subject as the OpenPanel profile with its readable
  // traits (email, name, account slug, organization) and link it to the
  // account group, plus the five standard global properties. Email and name
  // are profile traits only, never event properties. `user` is
  // rebuilt on every token refresh and re-auth, so this re-applies whenever the
  // claims change and clears the identity on sign-out. Repeat calls with an
  // unchanged identity are no-ops inside the analytics module.
  useEffect(() => {
    if (user?.sub) {
      // Consent gate first (task 461): fetch the User's `analytics_tracking`
      // preference (session-cached — one request per session) and only then
      // apply the identity, so an opted-out User never gets an op.identify().
      // The fetch failure path leaves the gate at its default (opted in, per
      // the opt-out model) and still applies identity.
      const identity = {
        profileId: user.sub,
        accountId: user.accountId,
        // Endpoint-sourced (task 676): `undefined` until the usage query
        // answers, so the identity re-applies with the real plan once it does.
        plan: analyticsPlan,
        platformRoles: user.platformRoles,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        account_slug: user.accountSlug,
        organization_name: user.organizationName,
      };
      void syncAnalyticsConsent().finally(() => {
        applyAnalyticsIdentity(identity);
      });
    } else {
      clearPreferenceCache();
      resetAnalyticsIdentity();
    }
  }, [user, analyticsPlan]);

  // ── Auth actions ──────────────────────────────────────────────────────────

  const login = useCallback(() => {
    keycloak.login({ redirectUri: window.location.href });
  }, []);

  const loginWithOptions = useCallback(
    (options?: { action?: 'register'; loginHint?: string; redirectUri?: string }) => {
      keycloak.login({
        redirectUri: options?.redirectUri ?? window.location.href,
        ...(options?.action ? { action: options.action } : {}),
        ...(options?.loginHint ? { loginHint: options.loginHint } : {}),
      });
    },
    []
  );

  const refreshToken = useCallback(async (): Promise<void> => {
    // Force-refresh unconditionally. Throws on failure.
    await keycloak.updateToken(-1);
    applyToken();
  }, [applyToken]);

  const logout = useCallback(() => {
    sessionStorage.removeItem('kc_token');
    sessionStorage.removeItem('kc_refreshToken');
    sessionStorage.removeItem('kc_idToken');
    setUser(null);
    setToken(null);
    // Clear React Query cache so no stale data leaks to the next session.
    queryClient.clear();
    keycloak.logout({ redirectUri: window.location.origin });
  }, [queryClient]);

  const clearAuthError = useCallback(() => {
    setAuthError(null);
  }, []);

  // ── Context value ─────────────────────────────────────────────────────────

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated,
    // Provisioned only when the token says so AND the API hasn't rejected an
    // account-scoped call with 403 MISSING_ACCOUNT_SCOPE.
    isAccountProvisioned: Boolean(user?.isAccountProvisioned) && !accountScopeRejected,
    isAmsUnavailable: Boolean(user?.isAmsUnavailable),
    isLoading,
    login,
    loginWithOptions,
    logout,
    refreshToken,
    authError,
    clearAuthError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
